'use client'

import Link from "next/link";
import { TemplateItem } from "@plura/shared/lead-forms";
import { Button } from "@plura/client/components";
import { convertToTitleCase } from "@plura/shared/utils";

interface Props {
  className?: string;
  template: TemplateItem;
  onPreview: () => void;
  onUseTemplate?: (template: TemplateItem) => void;
  isProcessing?: boolean;
  withLinks?: boolean;
  isRecommended?: boolean;
}

const TemplateCard = ({ className, template, onUseTemplate, onPreview, isProcessing, withLinks, isRecommended }: Props) => {
  const { id, title, description, image, tags, popular } = template;

  const showOverlay = !withLinks;
  const forceVisible = isProcessing && !withLinks;

  const content = (
    <>
      <div className="relative">
        {showOverlay && (
          <>
            <div className={`absolute inset-0 rounded-3xl bg-black/60 transition-all pointer-events-none z-1 ${forceVisible ? 'visible opacity-100' : 'invisible opacity-0 group-hover:visible group-hover:opacity-100'}`} />
            <div className={`absolute top-1/2 left-1/2 -translate-1/2 z-3 flex flex-col items-center gap-2.5 transition-all ${forceVisible ? 'visible opacity-100' : 'invisible opacity-0 group-hover:visible group-hover:opacity-100'}`}>
              <Button
                isBlack
                size="medium"
                className="w-full"
                onClick={() => {
                  if (onUseTemplate) onUseTemplate(template);
                }}
                processing={isProcessing}
              >
                Use Template
              </Button>
              <Button
                isWhite
                size="medium"
                className="w-full"
                onClick={() => {
                  if (onPreview) onPreview();
                }}
              >
                Preview
              </Button>
            </div>
          </>
        )}

        <img
          className="w-full h-auto rounded-3xl object-cover transition-transform group-hover:scale-[1.02]"
          src={`${image}?q=70&w=900`}
          alt={title}
        />
      </div>
      <div className="p-4 max-xl:p-3">
        <div className="text-sub-title-1 text-t-primary truncate">{title}</div>
        <div className="text-caption text-t-tertiary/80 mt-0.5 line-clamp-2">{description}</div>
        {tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {tags.map((tag) => (
              <span key={tag} className="label label-gray text-caption">
                {convertToTitleCase(tag)}
              </span>
            ))}
            {isRecommended && <span className="label label-blue text-caption">Recommended</span>}
            {popular && !isRecommended && <span className="label label-green text-caption">Popular</span>}
          </div>
        )}
      </div>
    </>
  );

  if (withLinks) {
    return (
      <Link href={`/templates/${id}`} className={`group flex flex-col gap-2 p-2 pb-4 bg-b-surface2 rounded-4xl transition-shadow hover:shadow-depth ${className || ''}`}>
        {content}
      </Link>
    );
  }

  return (
    <div className={`group flex flex-col gap-2 p-2 pb-4 bg-b-surface2 rounded-4xl transition-shadow hover:shadow-depth ${className || ''}`}>
      {content}
    </div>
  );
}

export default TemplateCard;
